import { useEffect, useRef, useState } from "react";
import { useTexte, WORTLAUT } from "../../texte";
import { useUhrwechsel } from "../../hooks/useUhrwechsel";
import { useEscape } from "../../hooks/useEscape";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import {
  useDeleteMessages,
  useMoveMessages,
  usePublishToAll,
  useEntwurf,
  useMarkRead,
  useMembers,
  useMessages,
  useHistoryTrimmedOnLeaving,
  useOlderMessages,
  useThreads,
  useTrip,
  useTagestrennerAendern,
  useWetterkarteAendern,
  useGifTauschen,
  useEditCollage,
} from "../../hooks/queries";
import { ApiError, fehlertext, ohneNetz } from "../../api/client";
import { vergissOrt } from "../../lib/letzterChat";
import { merkeUebergabe } from "../../lib/highlightUebergabe";
import type { Media, Member, Message } from "../../api/types";
import { dayLabel, dayMoment, sameDay, tagKey } from "../../lib/format";
import { imVerlauf } from "../../lib/nachricht";
import { filterArten, neuAusgeblendet, sichtbar, useChatfilter } from "../../lib/chatfilter";
import { Chatfilter } from "./Chatfilter";
import { useVerlaufsprung } from "../../hooks/useVerlaufsprung";
import { useWartend } from "../../lib/eingabevorrat";
import { Tageswahl } from "./Tageswahl";
import { Tagestrennerwahl } from "./Tagestrennerwahl";
import { Wetterwahl } from "./Wetterwahl";
import { Abstimmungsformular } from "./Abstimmungsformular";
import { Gifwahl } from "./Gifwahl";
import { CollageSheet } from "./CollageSheet";
import { ReisenIcon, GruppeIcon, InfoIcon, FilterIcon } from "../../components/Icons";
import { Netzstreifen } from "../../components/Netzstreifen";
import { Zonenvorschlag } from "../trips/Zonenvorschlag";
import { Auswahlleiste } from "./Auswahlleiste";
import { Verschiebeleiste } from "./Verschiebeleiste";
import { Verschiebefrage } from "./Verschiebefrage";
import { beginMove, endMove, useMove } from "../../lib/moveState";
import { datumText } from "../../lib/ortszeit";
import type { Insertion } from "../../lib/insertion";
import { rueckfrage } from "../../components/Rueckfrage";
import { saveMedia, saveMediaWords } from "./saveMedia";
import { useOnline } from "../../hooks/useOutbox";
import { Reiseumschalter, zeigtEntwurf, zeigtPlaylist, zeigtTage } from "../map/Reiseumschalter";
import { Veroeffentlichen } from "./Veroeffentlichen";
import { useOutbox, useWartendeAbstimmungen, useWartendesWetter } from "../../hooks/useOutbox";
import { AntwortenKnopf } from "./Antworten";
import { Bubble } from "./Bubble";
import { Chathintergrund } from "./Chathintergrund";
import { Composer } from "./Composer";
import { useMeldezeile } from "./useMeldezeile";
import { Wartend } from "./Wartend";
import { Wetterwartet } from "./Wetterwartet";
import { Abstimmungwartet } from "./Abstimmungwartet";
import { jumpBegins } from "../../lib/jumpState";
import { useStableCallbacks } from "../../hooks/useStableCallbacks";

/** Worauf sich die Nachricht bezieht, die gerade geschrieben wird. */
export interface Bezug {
  message: Message;
  /** Gesetzt, wenn es um genau ein Foto des Beitrags geht. */
  mediaId?: number;
}

/**
 * Ein Chat.
 *
 * Ohne `userId` ist das die Reise selbst: für einen Empfänger der Einzelchat
 * mit dem Autor, für den Absender sein Travel Broadcast. Mit `userId` der
 * Antwortverlauf eines Empfängers -- den kann nur der Absender öffnen, und
 * das Backend prüft das noch einmal selbst.
 */
/**
 * **And since 1.21.0 the draft of the report** (#85): the same page under
 * `/t/:tripId/entwurf`, with the draft as its list. One page and not two,
 * because everything a draft can do -- bubbles, media, cards, the line, the
 * basket -- is what the chat does; only the list, the read mark and the
 * menu entries differ, and those hang on `entwurf`.
 */
export function ThreadPage({ entwurf = false }: { entwurf?: boolean } = {}) {
  const t = useTexte();
  const params = useParams();
  const navigate = useNavigate();
  const tripId = Number(params.tripId);
  const withUser = params.userId ? Number(params.userId) : undefined;

  /*
   * **Wer Uhrzeiten in der Uhr des Geraets zeigt, hoert auf die Uhr** (#73).
   * Der Rueckgabewert wird nicht gelesen; er aendert sich beim Zonenwechsel
   * und zeichnet damit die Seite neu -- Tagestrenner und die wartenden
   * Beitraege. Die Sprechblasen sind `memo` und hoeren seit 1.156.0 selbst
   * auf die Uhr (`Bubble`). Ein Haken in `Shell` reichte nicht bis
   * hierher: react-router gibt dem `<Outlet/>` dasselbe Element zurueck, und
   * React ueberspringt den Teilbaum.
   */
  useUhrwechsel();

  const trip = useTrip(tripId);
  // Both hooks always run (hooks never sit behind a condition); only one is on.
  const chat = useMessages(tripId, withUser, !entwurf);
  // Leaving the chat keeps only its newest page (1.111.0): the next visit
  // draws forty posts, not every page a jump loaded.
  useHistoryTrimmedOnLeaving(tripId, withUser, entwurf);
  const entwurfsliste = useEntwurf(tripId, entwurf);
  const messages = entwurf ? entwurfsliste : chat;
  // Where unsent input waits (1.22.0) -- the dot in the pill.
  const wartend = useWartend(tripId);
  const older = useOlderMessages(tripId, withUser);
  const markRead = useMarkRead(tripId, withUser);
  // Der Name des Empfängers kommt aus der Teilnehmerliste, nicht aus den
  // Nachrichten: wer noch nie geantwortet hat, käme darin gar nicht vor.
  const members = useMembers(tripId, withUser !== undefined);
  // Nur aus dem Zwischenspeicher gelesen -- die Liste nebenan fragt ohnehin.
  const threads = useThreads();
  // Was in diesem Verlauf noch auf Netz wartet -- steht unten, vor der
  // Eingabezeile, als wäre es schon gesendet. Denn das ist es aus Sicht
  // dessen, der es geschrieben hat.
  const wartende = useOutbox(tripId, withUser, entwurf);
  // Weather cards waiting for the net (#98): only in the trip's own chat or draft, never in a private thread.
  const wartendesWetter = useWartendesWetter(tripId, entwurf);
  // Polls waiting for the net (1.80.0): like the weather, only in the trip's own chat or draft.
  const wartendeAbstimmungen = useWartendeAbstimmungen(tripId, entwurf);
  /*
   * **The filter of the chat** (1.93.0, #114): a view in the browser, per
   * trip and chat on this device (`lib/chatfilter.ts`). The refs give the
   * jump the newest page and filter -- `springeZu()` retries after a draw,
   * and the closure of the draw it began in would still see the old ones.
   */
  const filterWahl = filterArten(trip.data?.role, withUser, entwurf);
  const [filter, setFilter] = useChatfilter(tripId, withUser, filterWahl);
  const [filterBlatt, setFilterBlatt] = useState(false);
  const filterJetzt = useRef(filter);
  filterJetzt.current = filter;
  const datenJetzt = useRef(messages.data);
  datenJetzt.current = messages.data;
  const neuesteId = messages.data?.messages[0]?.id ?? 0;
  const filterSetzen = (art: "beitraege" | "privat" | null) => setFilter(art, neuesteId);

  /** The day sheet (1.19.0): the day of the divider that opened it, or closed. */
  const [tageswahl, setTageswahl] = useState<string | null>(null);
  /**
   * The day separator to change (#113): its sheet, prefilled. A new one comes
   * from the plus menu since 1.115.0 (#130) -- a separator is a post of its
   * own, deleted like any other.
   */
  const [trennerAenderung, setTrennerAenderung] = useState<Message | null>(null);
  const trennerAendern = useTagestrennerAendern(tripId, withUser, entwurf);
  const [trennerFehler, setTrennerFehler] = useState<string | null>(null);
  // The one message line (1.80.0), here since 1.117.4: the bubbles' menus
  // report to it too, and it goes with the net and with the next choice.
  const meldezeile = useMeldezeile();
  /**
   * The weather card to change (1.86.0): the sheet of "Wetter an Ort und
   * Zeit", prefilled with the card. Only online -- the server asks Apple.
   */
  const [wetterAenderung, setWetterAenderung] = useState<Message | null>(null);
  const wetterAendern = useWetterkarteAendern(tripId, withUser, entwurf);
  /** The poll to change (1.86.0): the poll's own sheet, prefilled. */
  const [abstimmungAenderung, setAbstimmungAenderung] = useState<Message | null>(null);
  /** The day card to compute anew (1.86.0): handed to the composer, where the sheet and the drawing live. */
  const [tagAenderung, setTagAenderung] = useState<Message | null>(null);
  /** The post of nothing but a GIF that gets another one (1.86.0). */
  const [gifAenderung, setGifAenderung] = useState<Message | null>(null);
  const gifTauschen = useGifTauschen(tripId, withUser, entwurf);
  /** The collage to change (1.168.0): its own sheet, with its photos and style. */
  const [collageAenderung, setCollageAenderung] = useState<Message | null>(null);
  const [collageFehler, setCollageFehler] = useState<string | null>(null);
  const collageAendern = useEditCollage(tripId, withUser, entwurf);
  /**
   * Where the next post goes (#130): before or after a chosen post, in the
   * chat and in the draft -- the marker above the line. Only with a net.
   */
  const [einfuegen, setEinfuegen] = useState<Insertion | null>(null);
  /** And which draft is about to be published (#85). */
  const [zuVeroeffentlichen, setZuVeroeffentlichen] = useState<Message | null>(null);

  // Worauf sich das bezieht, was gerade geschrieben wird. Der Zustand sitzt
  // hier und nicht im Composer: gesetzt wird er an einer Sprechblase weiter
  // oben, und die beiden kennen einander nur über diese Seite.
  //
  // Er steht bei den anderen Hooks und nicht weiter unten bei seinem Gebrauch:
  // darunter stehen frühe Rückgaben, und ein Hook hinter einer davon liefe
  // nicht bei jedem Durchgang.
  const [bezug, setBezug] = useState<Bezug | null>(null);
  /**
   * Der Auswahlmodus zum Löschen (#30) — `null` heißt: aus.
   *
   * **Er liegt hier und nicht an der Sprechblase**: eine Auswahl über mehrere
   * Nachrichten kann keine einzelne kennen. Die Blase bekommt nur, was sie
   * selbst angeht.
   */
  const [auswahl, setAuswahl] = useState<Set<number> | null>(null);
  // What the selection is for (1.117.3): deleting or moving, each its own
  // entry in the menu and its own symbol in the bar; since 1.167.0 also the
  // author's private messages to everyone.
  const [auswahlZweck, setAuswahlZweck] = useState<"loeschen" | "verschieben" | "anAlle">("loeschen");
  const loeschen = useDeleteMessages(tripId, withUser, entwurf);
  const online = useOnline();
  /**
   * Posts to another place (#130, 1.117.0): the author's posts to everyone,
   * in the trip's chat. Picked in the selection, then a target tapped -- the
   * waiting move lives in `lib/moveState.ts`, so that it survives the way to
   * the search and back.
   */
  // And in the draft the same way (1.135.0, from use: "auch das Verschieben
  // soll gleich funktionieren wie im Chat"); it replaced "Nach oben" and
  // "Nach unten". The server tells the two apart by the target.
  const kannBewegen = trip.data?.role === "sender" && withUser === undefined;
  const bewegung = useMove(tripId, entwurf);
  const wartendeBewegung = kannBewegen ? bewegung : null;
  const bewegen = useMoveMessages(tripId);
  const anAlle = usePublishToAll(tripId, withUser);
  const [bewegeZiel, setBewegeZiel] = useState<Message | null>(null);
  const [bewegeFehler, setBewegeFehler] = useState<string | null>(null);
  const [loeschbefund, setLoeschbefund] = useState<string | null>(null);

  /**
   * Escape beendet die Auswahl — dieselbe Taste wie überall im Projekt.
   *
   * Und **ein Wechsel des Verlaufs auch**: eine Auswahl aus einem anderen
   * Chat wäre eine Liste von Kennungen, die hier nichts bedeuten.
   */
  useEffect(() => {
    setAuswahl(null);
    setLoeschbefund(null);
  }, [tripId, withUser]);

  // **A filter change ends the selection** (1.93.0, #114): a picked row the
  // filter now hides would be deleted without being seen.
  useEffect(() => {
    setAuswahl(null);
  }, [filter?.art]);

  /*
   * **A short filtered list loads older pages by itself** (1.93.0, #114).
   * Older pages come only from the button, the jump and the edge search; a
   * reader with a few private lines among hundreds of posts would see two
   * rows and a button. Up to ten pages per filter change, until twenty rows
   * stand -- `useOlderMessages` takes care that no page comes twice.
   */
  const nachladeRunden = useRef(0);
  useEffect(() => {
    nachladeRunden.current = 0;
  }, [filter?.art, tripId, withUser]);
  useEffect(() => {
    if (filter === null || older.isPending) return;
    const cursor = messages.data?.next_before ?? null;
    if (cursor === null || nachladeRunden.current >= 10) return;
    const gezeigt = (messages.data?.messages ?? []).filter((m) => imVerlauf(m) && sichtbar(m, filter)).length;
    if (gezeigt >= 20) return;
    nachladeRunden.current += 1;
    older.mutate(cursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, older.isPending, messages.data?.next_before]);

  // **Through the stack of `useEscape()`** (1.167.0): an own listener in the
  // capture phase took the key from the question above it, and Escape on
  // "Wirklich löschen?" or "an alle senden?" ended the whole selection.
  useEscape(auswahl === null ? null : () => setAuswahl(null));

  // Beim Wechsel des Chats fällt der Bezug weg -- eine Nachricht aus der
  // vorigen Reise wäre hier weder zu sehen noch zulässig.
  useEffect(() => {
    setBezug(null);
    setEinfuegen(null);
    setZuVeroeffentlichen(null);
  }, [tripId, withUser, entwurf]);

  /**
   * Springen, Lesemarke und die Kante „ab hier ungelesen" --
   * `useVerlaufsprung.ts`.
   *
   * Elf Effekte hatte diese Seite, und sechs davon gehörten zusammen: wohin
   * gesprungen wird, wann nur einmal, wie weit rückwärts gesucht wird und was
   * als gelesen gemeldet ist. Hier bleiben die Liste, die Trenner und das
   * Menü.
   */
  const sprung = useVerlaufsprung({
    tripId,
    withUser,
    entwurf,
    messages,
    older,
    markRead,
    wartende,
    filter: {
      sichtbar: (m) => sichtbar(m, filter),
      istVersteckt: (id) => {
        const jetzt = filterJetzt.current;
        if (jetzt === null) return false;
        const ziel = datenJetzt.current?.messages.find((m) => m.id === id);
        return ziel !== undefined && !sichtbar(ziel, jetzt);
      },
      aufheben: () => setFilter(null, 0),
    },
  });
  const { verlauf, verlaufScrollt, nachUntenSpringen, springeZu, ersteUngelesene, kanteAufgegeben } = sprung

  /**
   * The bubbles' callbacks, one set with a stable identity (1.155.0,
   * review D4). `Bubble` is `memo`; a callback written inline below would
   * be a new function on every drawing and make every row draw on every
   * tick. The wrappers keep their identity, what they call is the newest
   * closure (`useStableCallbacks`). Which of them a row gets is decided
   * below per row, as before: the author in the trip chat, the open trip,
   * the draft -- a condition yields the wrapper or `undefined`.
   */
  const autorImChat = trip.data?.role === "sender" && withUser === undefined;
  // A private chat of the author in an open trip (1.157.0).
  const autorImPrivatchat = trip.data?.role === "sender" && withUser !== undefined && !entwurf && trip.data?.archived_at == null;
  /** Only online -- each of these asks the server about something that stands. */
  // In the draft too (1.135.0, from use: "im Entwurfsmodus lassen sich
  // Objekte nicht ändern"): each change answers with the whole post, set in
  // by id in the list it came from.
  const mitNetz = (tun: () => void, hinweis: string) => {
    setTrennerFehler(null);
    if (!online) {
      meldezeile.melde("menue", hinweis, "fehler", true);
      return;
    }
    tun();
  };
  /**
   * The author's private messages to everyone (1.157.0, a selection since
   * 1.167.0): asked first, then moved, and the chat jumps to the first new
   * post in the trip chat -- the others stand below it. **Nothing goes if
   * one does not**, so on an error the selection stays as it was and the
   * line says so.
   */
  const anAlleSenden = async (ids: number[]) => {
    const ja = await rueckfrage(
      t.chat.publishToAll.frage(t.chat.auswahlWorte(ids.length, 0)),
      t.chat.publishToAll.knopf,
      { details: [t.chat.publishToAll.erklaerung(ids.length)], art: "normal", merkmal: "publish-to-all" },
    );
    if (!ja) return;
    setLoeschbefund(null);
    try {
      const antwort = await anAlle.mutateAsync(ids);
      setAuswahl(null);
      jumpBegins();
      navigate(`/t/${tripId}?m=${antwort.ids[0]}`);
    } catch (fehler) {
      setLoeschbefund(t.chat.publishToAll.fehler(ids.length, fehlertext(fehler)));
    }
  };
  /**
   * Save the photos and videos of a post (1.159.0): fetched under a sheet
   * with a bar (1.160.0), then handed on (`saveMedia()`, which asks for a second tap on
   * iOS). The sentence afterwards says where it went, as for a photo.
   */
  const medienSichern = async (medien: Media[]) => {
    const eines = medien.length === 1;
    // The sheet with its bar says it is loading (1.160.0); the line under the
    // chat only says where it went, or that it failed.
    meldezeile.raeume("medien-sichern");
    try {
      const weg = await saveMedia(medien, saveMediaWords(t, medien.length));
      if (weg === "geladen") meldezeile.melde("medien-sichern", t.chat.inDownloads, "hinweis");
      else meldezeile.raeume("medien-sichern");
    } catch {
      meldezeile.melde("medien-sichern", eines ? t.chat.videoNichtGeholt : t.chat.medienNichtGeholt, "fehler");
    }
  };
  const rueckrufe = useStableCallbacks({
    onSaveMedia: (medien: Media[]) =>
      mitNetz(() => void medienSichern(medien), medien.length === 1 ? t.chat.videoOhneNetz : t.chat.medienOhneNetz),
    onPublishToAll: (id: number) => {
      setLoeschbefund(null);
      setAuswahlZweck("anAlle");
      setAuswahl(new Set([id]));
    },
    // Before or after this post (#130): only with a net.
    onEinfuegen: (ziel: Message, where: "before" | "after") =>
      mitNetz(() => setEinfuegen({ id: ziel.id, where }), t.chat.einfuegen.brauchtNetz),
    onVeroeffentlichen: (ziel: Message) => setZuVeroeffentlichen(ziel),
    onReply: (ziel: Message, mediaId?: number) => setBezug({ message: ziel, mediaId }),
    onJump: (id: number) => springeZu(id),
    // The day separator's sheet (#113): only with a net.
    onTagestrenner: (ziel: Message) => mitNetz(() => setTrennerAenderung(ziel), t.chat.tagestrenner.ohneNetz),
    waehle: (id: number) =>
      setAuswahl((alt) => {
        const neu = new Set(alt ?? []);
        if (neu.has(id)) neu.delete(id);
        else neu.add(id);
        return neu;
      }),
    onLoeschmodus: (id: number) => {
      setLoeschbefund(null);
      setAuswahlZweck("loeschen");
      setAuswahl(new Set([id]));
    },
    onMenueAuf: () => {
      meldezeile.neueWahl();
      setTrennerFehler(null);
    },
    onVerschiebemodus: (id: number) => {
      setLoeschbefund(null);
      setAuswahlZweck("verschieben");
      setAuswahl(new Set([id]));
    },
    ziel: (ziel: Message) => {
      setBewegeFehler(null);
      setBewegeZiel(ziel);
    },
    onWetter: (ziel: Message) => mitNetz(() => setWetterAenderung(ziel), t.chat.wetterOhneNetz),
    onAbstimmung: (ziel: Message) => mitNetz(() => setAbstimmungAenderung(ziel), t.allgemein.ohneNetz),
    onTag: (ziel: Message) => mitNetz(() => setTagAenderung(ziel), t.chat.tagOhneNetz),
    onGif: (ziel: Message) => mitNetz(() => setGifAenderung(ziel), t.chat.gifOhneNetz),
    onCollage: (ziel: Message) =>
      mitNetz(() => {
        setCollageFehler(null);
        setCollageAenderung(ziel);
      }, t.chat.collage.ohneNetz),
    // Nur der Autor, und nur im Reise-Chat: was in einem Antwortverlauf
    // steht, ist die Antwort eines Einzelnen und gehört nicht auf eine
    // Karte, die alle sehen.
    onAufDieKarte: (ziel: Message) => {
      merkeUebergabe({
        tripId,
        // **Der Beitrag ist der Text, nicht der Titel.**
        //
        // Hier stand die erste Zeile als Titel und der Rest als Text --
        // „ein Reisebericht fängt fast immer mit einer an". Tut er nicht:
        // die meisten Beiträge sind ein einziger Absatz, und der landete
        // dann vollständig in der Titelzeile, während der Text leer blieb.
        // Aus dem Betrieb gemeldet.
        //
        // Der Titel bleibt leer und füllt sich mit dem Ortsnamen, sobald
        // er da ist -- derselbe Weg wie bei „Aus Foto/Video".
        title: "",
        body: ziel.body,
        at: ziel.created_at,
        // The zone the post was written in (1.86.0).
        tz_offset_min: ziel.tz_offset_min,
        // The post's attachments go along (1.16.0): the map shows them in
        // the form and attaches them on save. Where and when they were
        // taken the map looks up itself -- the chat does not know it.
        //
        // **A voice message goes along too** (1.35.0). It was dropped here
        // with the reason "a voice message is no picture", and that was
        // true and beside the point: the menu entry moves a post onto the
        // map, not a picture. What stayed behind was a recording nobody
        // could hear again.
        media: ziel.media,
      });
      // In die Werkbank, nicht in die Ansicht: ein Entwurf ist Werkzeug,
      // und das steht seit der Trennung nur im Reiter „Bearbeiten".
      navigate(`/t/${tripId}/karte/bearbeiten`);
    },
  });

  // Ein fehlgeschlagener Abruf darf nicht wie ein leerer Chat aussehen --
  // sonst wirkt eine verweigerte Berechtigung wie „hier steht eben nichts“.
  //
  // **Ein Netzfehler gehört aber nicht dazu.** Aus dem Betrieb: im Flugmodus
  // stand hier „Dieser Chat lässt sich nicht öffnen", und damit war auch die
  // Eingabezeile weg -- also ausgerechnet der Weg in den Ausgangskorb, den es
  // für genau diesen Fall gibt. Ohne Empfang bleibt der Chat offen: was
  // geladen ist, bleibt lesbar, und schreiben geht weiter.
  const failure = trip.error ?? messages.error;

  // Eine Reise, die es nicht mehr gibt, ist keine Meldung wert -- nur der Weg
  // zurück.
  //
  // Aus dem Betrieb, auf dem iPhone: die Web-App nimmt beim Öffnen die zuletzt
  // besuchte Seite wieder auf, und die zeigte auf eine gelöschte Reise. Beim
  // Start stand dann „Dieser Chat lässt sich nicht öffnen" — für etwas, das
  // niemand angeklickt hatte. Wer die Reise verlassen hat oder aus ihr
  // entfernt wurde, bekommt vom Server denselben Bescheid; auch dann gehört er
  // heraus und nicht in eine Sackgasse. Wohin, entscheidet dann der
  // Startbereich -- mit gelöschter Erinnerung ist das der oberste Chat.
  //
  // Alles andere (ein Serverfehler etwa) bleibt stehen und wird gesagt: dazu
  // gibt es nichts Besseres zu tun, und stillschweigend zu verschwinden wäre
  // die schlechtere Auskunft.
  const verschwunden = failure instanceof ApiError && failure.status === 404;
  useEffect(() => {
    if (verschwunden) vergissOrt();
  }, [verschwunden]);
  if (verschwunden) return <Navigate to="/" replace />;

  /*
   * **A server that stumbles is not a chat that cannot be opened** (1.78.0).
   * A 502 or 504 of the host during a poll replaced the loaded thread and the
   * composer with "lässt sich nicht öffnen" until a poll went through again --
   * and the composer is the way into the outbox. With the thread loaded,
   * a 5xx keeps it standing, and a line above the composer says it; a
   * refusal (4xx) still closes it, as before.
   */
  const stockt =
    failure instanceof ApiError
    && failure.status >= 500
    && trip.data !== undefined
    && messages.data !== undefined;

  if (failure && !ohneNetz(failure) && !stockt) {
    return (
      <div className="empty">
        <p className="empty__title">{t.chat.nichtZuOeffnen}</p>
        <p>{failure.message}</p>
        <Link className="button button--quiet" to="/">
          {t.chat.zurueckZurListe}
        </Link>
      </div>
    );
  }

  /**
   * Der Fehlschlag beim Laden des Verlaufs — **oder `null`**.
   *
   * **Gefragt wird nach `failureReason` und nicht nach `isError`**, und das ist
   * der Kern der Meldung aus dem Betrieb. Die Abfrage traegt
   * `refetchInterval` und wiederholt sich; ein Serverfehler bringt sie damit
   * nie in den Endzustand „fehlgeschlagen" -- gemessen: siebenunddreissig
   * Versuche in achtundvierzig Sekunden, und `isPending` blieb dabei die ganze
   * Zeit wahr. Eine Meldung, die an `isError` haengt, kommt in diesem Fall
   * **nie**.
   *
   * `failureReason` traegt den letzten Fehlschlag, auch waehrend noch
   * weiterversucht wird. Genau das ist die ehrliche Auskunft: es wurde
   * versucht, es kam nichts, und weiter versucht wird trotzdem.
   *
   * **Erst nach den Wiederholungen**, nicht beim ersten Zucken: ein Aufblitzen
   * bei jedem kurzen Ruckler waere schlimmer als das Warten.
   *
   * **Und die Zahl ist zwei, nicht drei.** Die Regel in `main.tsx` gibt auf,
   * sobald `failureCount < 2` nicht mehr gilt -- gezaehlt wird also 1, dann 2,
   * und dann ist Schluss. Hier stand erst `>= 3`; die Schwelle wurde nie
   * erreicht, und die Meldung kam nie. Gemessen, nicht ueberlegt.
   *
   * **Und ohne Netz gilt es nicht.** Dafuer gibt es den eigenen Satz weiter
   * unten -- dort ist die Verbindung weg, hier hat der Server nein gesagt.
   */
  const ladefehler =
    messages.data === undefined
    && messages.failureCount >= 2
    && messages.failureReason !== null
    && !ohneNetz(messages.failureReason)
      ? messages.failureReason
      : null;

  const partner = members.data?.find((m) => m.user.id === withUser);
  const partnerName = partner?.user.display_name;

  // Warum hier nichts zu schreiben ist -- oder null, wenn es geht.
  //
  // Beurteilt wird erst, wenn die Teilnehmerliste **da** ist: solange sie
  // lädt, ist „nicht gefunden" kein Befund, sondern ein Zwischenstand, und
  // „nicht mehr in dieser Reise" blitzte bei jedem Öffnen kurz auf.
  const stumm =
    withUser === undefined || !members.isSuccess
      ? null
      : grundOhneZeile(partner);

  // Und solange es noch offen ist, steht unten gar nichts. Eine Eingabezeile,
  // die eine Zehntelsekunde später verschwindet, ist dieselbe Verwirrung in
  // klein -- lieber einen Augenblick nichts als kurz ein falsches Versprechen.
  const wartetAufAuskunft = withUser !== undefined && members.isPending;

  // Ohne Netz weiß die Reise selbst nichts mehr zu sagen -- die Chatliste
  // daneben aber schon: sie trägt Titel und Namen und liegt im
  // Zwischenspeicher. Ein Kopf mit „…" wäre schlechter als einer aus zweiter
  // Hand.
  const ausListe = threads.data?.find(
    (t) => t.trip_id === tripId && (t.with_user?.id ?? undefined) === withUser,
  );

  const title = entwurf
    ? t.chat.entwurf.titel
    : withUser
      ? (partnerName ?? ausListe?.with_user?.display_name ?? "Antworten")
      : (trip.data?.title ?? ausListe?.trip_title ?? "…");

  // Ohne Angaben zur Reise wird nichts behauptet: „Reisebericht von" mit
  // leerem Namen dahinter sah aus wie ein Fehler und war nur fehlender Empfang.
  // Dass kein Netz da ist, sagt der Streifen über der Eingabezeile -- hier
  // stünde es ein zweites Mal.
  const zweck = entwurf
    ? t.chat.entwurf.zweck(trip.data?.title ?? ausListe?.trip_title ?? "")
    : trip.data === undefined
      ? ""
      : withUser
        ? `Antworten in „${trip.data.title}“ – nur für dich sichtbar`
        : trip.data?.role === "sender"
          ? // **„Travel Broadcast" statt „Rundbrief"** — so aus dem Betrieb
            // gewünscht. Es klingt nach Serienbrief und Verwaltung; das Ding
            // heißt so, wie die Anwendung heißt.
            //
            // **Und das gilt inzwischen auch für Kommentare und Doku.** Die
            // Oberfläche sagte es längst richtig, im Code lebte das alte Wort
            // vierunddreißig Mal weiter. Diese Stelle hier ist die einzige,
            // an der es stehen bleibt — sie ist der Beleg für die
            // Entscheidung. Sonst heißt es **Beitrag** (die Nachricht) oder
            // **an alle** (die Reichweite); die Regel steht in `CLAUDE.md`.
            `Travel Broadcast an ${(trip.data.member_count ?? 1) - 1} Empfänger` +
            // Wer wartet, wartet auf eine Entscheidung -- und die trifft niemand,
            // der nichts davon weiß. Die Reiseseite ist einen Tipp entfernt.
            (trip.data.pending_requests > 0
              ? ` · ${trip.data.pending_requests} ${trip.data.pending_requests === 1 ? "Anfrage" : "Anfragen"}`
              : "")
          : `Reisebericht von ${trip.data?.owner.display_name ?? ""}`;

  // Die Beschreibung stand bisher nur auf der Infoseite -- also dort, wo man
  // sie beim Lesen nie sieht. Sie gehört in den Kopf des Chats, denn sie sagt,
  // worum es in dieser Reise geht.
  //
  // **Eine Zeile unter dem Titel, immer dieselbe Höhe** (1.50.0, from use:
  // "die Kopfzeile der Reise ist unterschiedlich hoch", and the decision
  // "nur eine Zeile"). Until then the description had two lines of its own
  // on a wide screen and none on a phone, so heads of different trips sat at
  // different heights next to each other. Now the description takes the one
  // line when there is one, cut with an ellipsis and readable in full on the
  // info page; otherwise the line says what the chat is (reach, or whose
  // report). A waiting request wins over the description: it asks for a
  // decision, and the info page is one tap away.
  const beschreibung = withUser ? "" : (trip.data?.description ?? "").trim();
  // **While filtered, the line says so** (1.93.0, #114): what stands and how
  // many new ones are hidden -- a line that never scrolls away, decided from
  // use over a mark in the pill, which steps aside while one reads.
  const zeile = filter !== null
    ? t.chat.filter.hinweis(filter.art, neuAusgeblendet(messages.data?.messages ?? [], filter, imVerlauf))
    : beschreibung !== "" && (trip.data?.pending_requests ?? 0) === 0 ? beschreibung : zweck;
  const kopfzeileArt = filter !== null ? "filter" : zeile === beschreibung ? "beschreibung" : "zweck";

  /**
   * **Eine gelöschte Nachricht steht nicht mehr da — auch nicht als Lücke.**
   *
   * Hier stand einmal „Diese Nachricht wurde gelöscht." Aus dem Betrieb
   * gewünscht: löschen heißt weg, nicht „hier war etwas". Ein Grabstein sagt
   * mehr über den Vorgang als das, was gelöscht wurde.
   *
   * **An der Datenbank ändert das nichts.** Der Server führt die Zeile
   * weiter mit `deleted_at`, liefert sie weiter aus und hält damit Zählung,
   * Lesestände und Zitate zusammen. Nur angezeigt wird sie nicht mehr.
   *
   * **Und eine Nachricht, die nichts zu zeichnen hat, ebenso** (1.20.1):
   * kein Text, kein Bild, keine Karte -- `Bubble` malte für sie eine leere
   * Zeile, und der Tagestrenner darüber blieb stehen. Aus dem Betrieb, mit
   * Bildschirmfoto: „MONTAG" über nichts. Woher solche Zeilen kommen, steht
   * an `istLeer()`.
   */
  const list = (messages.data?.messages ?? []).filter((m) => imVerlauf(m) && sichtbar(m, filter));

  const einfuegenText = einfuegen === null ? "" : (list.find((m) => m.id === einfuegen.id)?.body ?? "");
  /** The switch is off, and yet the draft is open -- from the pill, because drafts still wait. */
  const entwurfAus = entwurf && trip.data !== undefined && !trip.data.draft_chat;

  const olderCursor = messages.data?.next_before ?? null;

  return (
    <>
      <header className="bar">
        {/* The list of trips, not "back" (1.53.2): a suitcase, see `ReisenIcon`. */}
        <Link className="icon-button" to="/" aria-label={t.allgemein.zuDenReisen}>
          <ReisenIcon />
        </Link>
        <div className="bar__grow">
          <h2 className="bar__title">{title}</h2>
          {/* `title`: what the ellipsis cuts can at least be read in passing;
              the whole text stands on the info page. */}
          <p className="bar__subtitle" title={kopfzeileArt === 'beschreibung' ? beschreibung : undefined} data-kopfzeile={kopfzeileArt}>
            {zeile}
          </p>
        </div>
        {/* Nur beim Autor und nur im Reise-Chat: siehe `Antworten.tsx`. */}
        {withUser === undefined && !entwurf && trip.data?.role === "sender" && (
          <AntwortenKnopf tripId={tripId} />
        )}
        {/* **Und der Weg zurück** (0.45.0) -- aus dem Betrieb: wer über
            „Antworten" in den privaten Chat wechselt, soll von dort in den
            Reise-Chat zurückkommen, ohne den Umweg über die Liste. Nur beim
            Autor: ein Mitlesender hat keinen zweiten Chat, für ihn ist
            der Pfeil links der ganze Weg. */}
        {withUser !== undefined && trip.data?.role === "sender" && (
          <Link
            className="icon-button"
            to={`/t/${tripId}`}
            aria-label={t.chat.zumReiseChat}
            title={t.chat.zumReiseChat}
            data-zum-reise-chat=""
          >
            <GruppeIcon />
          </Link>
        )}
        {/* The funnel (1.93.0, #114): only where there is something to
            filter, filled while a filter is on. */}
        {filterWahl.length > 0 && (
          <button
            type="button"
            className="icon-button"
            onClick={() => setFilterBlatt(true)}
            aria-label={t.chat.filter.knopf}
            title={t.chat.filter.knopf}
            aria-pressed={filter !== null}
            data-chatfilter={filter?.art ?? "alle"}
          >
            <FilterIcon gefuellt={filter !== null} />
          </button>
        )}
        <Link
          className="icon-button"
          to={`/t/${tripId}/info`}
          aria-label={t.chat.angabenZurReise}
        >
          <InfoIcon />
        </Link>
      </header>
      {filterBlatt && (
        <Chatfilter
          arten={filterWahl}
          aktiv={filter?.art ?? null}
          onWahl={(art) => {
            filterSetzen(art);
            setFilterBlatt(false);
          }}
          onSchliessen={() => setFilterBlatt(false)}
        />
      )}

      {/* column-reverse: die neueste Nachricht steht unten, und der Browser
          hält den Blick von selbst dort, wenn eine dazukommt. */}
      {/* **Der Hintergrund liegt hinter dem Verlauf, nicht darin.**
          Ein eigenes Element und kein `background` am Verlauf selbst: der
          Deckgrad gilt dem Bild, nicht dem Text darüber. Läge das Bild als
          Hintergrund am Verlauf, müsste man die Schrift wieder heraufholen —
          und jede Sprechblase hätte ihre eigene Durchsicht.
          `aria-hidden`, denn es sagt nichts. */}
      {/* **Ein eigener Kasten um beides.** Der Hintergrund lag zuvor auf
          `inset: 0` der ganzen Seite und malte damit über die Kopfzeile und
          über die Eingabezeile — beide undurchsichtig und trotzdem weg. Hier
          reicht er nur noch so weit wie der Verlauf, den er hinterlegt. */}
      <div className="chatflaeche">
        {/* Nur im Reise-Chat, nicht im Antwortverlauf eines Empfängers: die
            Karte gehört zur Reise, und ein Umschalter „Chat · Karte" über einem
            Zweiergespräch verspräche einen Wechsel, den es dort nicht gibt.
            Seit 0.46.0 als Pille auf dem Verlauf statt als Zeile darüber --
            und sie weicht, solange der Verlauf scrollt. */}
        {withUser === undefined && (
          <Reiseumschalter
            tripId={tripId}
            aktiv={entwurf ? "entwurf" : "chat"}
            darfBearbeiten={trip.data?.role === "sender"}
            entwurf={zeigtEntwurf(trip.data)}
            playlist={zeigtPlaylist(trip.data)}
            tage={zeigtTage(trip.data)}
            versteckt={verlaufScrollt}
            wartend={wartend}
          />
        )}
        <Chathintergrund grund={trip.data?.chat_bg} />
        {/* The sign of a jump stands in `Shell` since 1.105.0
            (`JumpSign`): it must be there while another page still shows. */}

        <div className="timeline" ref={verlauf}>
          {messages.isPending && <p className="empty">{t.chat.wirdGeladen}</p>}

          {/*
            **Ein Fehlschlag beim Laden ist kein leerer Chat** (aus dem
            Betrieb).

            Hier stand nur `isPending` und danach der leere Fall -- und ein
            Serverfehler landete damit in „Noch nichts geschrieben". Das ist
            keine fehlende Auskunft, sondern eine **falsche**: die App
            behauptete, der Verlauf sei leer, obwohl sie ihn gar nicht
            bekommen hat. Gemeldet wurde es so: „während der Startbildschirm
            gezeigt wurde, habe ich auf Karte gedrückt, dann wurde angezeigt
            'Reise gibt es nicht' … der Fehler sollte dann auch im Chat
            abgefangen werden."

            **Ohne Netz bleibt beim eigenen Satz** -- der steht weiter unten
            und sagt etwas anderes: dort ist die Verbindung weg, hier hat der
            Server geantwortet und nein gesagt.

            **Und es gibt einen Weg heraus.** Ein Fehler ohne Knopf ist eine
            Sackgasse; auf der Karte war genau das der Unterschied.
          */}
          {ladefehler !== null && (
              <div className="empty" role="alert" data-schluessel="ladefehler">
                <p className="empty__title">{t.chat.ladefehler}</p>
                <p>{t.chat.ladefehlerText}</p>
                {ladefehler instanceof ApiError && (
                  <p className="leise">
                    {t.chat.ladefehlerNummer(ladefehler.status)}
                  </p>
                )}
                <button
                  className="button button--outline"
                  type="button"
                  onClick={() => void messages.refetch()}
                >
                  {t.chat.nochmal}
                </button>
              </div>
            )}

          {!messages.isPending &&
            ladefehler === null &&
            list.length === 0 &&
            wartende.length === 0 && (
              <div className="empty" data-schluessel="leer">
                {/* „Noch nichts geschrieben" wäre hier eine Behauptung über den
                Verlauf -- ohne Netz weiß die App darüber gerade nichts.

                **Der Schlüssel steht im DOM für den Prüfstand**: er muss
                messen können, dass dieser Block bei einem Fehlschlag gerade
                NICHT dasteht, ohne sich am Wortlaut festzuhalten. */}
                {messages.data === undefined && ohneNetz(messages.error) ? (
                  <>
                    <p className="empty__title">{t.chat.keinNetz}</p>
                    <p>{t.chat.keinNetzText}</p>
                  </>
                ) : filter !== null ? (
                  // **Under a filter, "nothing written yet" would be a claim
                  // about the chat** (1.93.0, #114): there may be plenty,
                  // only not of this kind. Say what is left out, and the way
                  // back.
                  <div data-schluessel="filterleer">
                    <p>{filter.art === "beitraege" ? t.chat.filter.leerBeitraege : t.chat.filter.leerPrivat}</p>
                    <button type="button" className="button button--outline" onClick={() => setFilter(null, 0)} data-chatfilter-aufheben="">
                      {t.chat.filter.alleZeigen}
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="empty__title">{t.chat.nochNichts}</p>
                    <p>
                      {entwurf
                        ? t.chat.entwurf.leer
                        : withUser
                        ? t.chat.nochKeineAntwort(partnerName ?? t.chat.diesePerson)
                        : trip.data?.role === "sender"
                          ? t.chat.schreibDieErste
                          : t.chat.wartetAufAutor}
                    </p>
                  </>
                )}
              </div>
            )}

          {/* Vor der Liste, weil die Zeitleiste umgekehrt läuft (column-reverse):
            was hier steht, erscheint unten -- und dort gehört das hin, was als
            Nächstes rausgeht. */}
          {[...wartende].reverse().map((eintrag) => (
            <Wartend key={`wartet-${eintrag.key}`} eintrag={eintrag} />
          ))}
          {withUser === undefined && [...(wartendesWetter ?? [])].reverse().map((eintrag) => (
            <Wetterwartet key={`wetter-${eintrag.key}`} eintrag={eintrag} versatz={trip.data?.zones ?? null} />
          ))}
          {withUser === undefined && [...(wartendeAbstimmungen ?? [])].reverse().map((eintrag) => (
            <Abstimmungwartet key={`abstimmung-${eintrag.key}`} eintrag={eintrag} />
          ))}

          {list.map((message, index) => {
            const next = list[index + 1];
            // The day a post stands under (#130): an inserted one the
            // chosen post's.
            const startsDay =
              !next || !sameDay(dayMoment(message), dayMoment(next));
            return (
              <div className="durchgereicht" key={message.id}>
                <Bubble
                  message={message}
                  tripId={tripId}
                  withUser={withUser}
                  // Für die Kärtchen einer Freigabe: sie zeigen ein Datum, und
                  // ein Datum ohne Uhr ist die halbe Angabe.
                  reiseVersatz={trip.data?.zones ?? null}
                  showAuthor={!message.own && Boolean(withUser)}
                  canReact={!entwurf && trip.data?.archived_at == null}
                  entwurf={entwurf}
                  // Before or after this post (#130): the author, the trip
                  // chat and its draft, an open trip, and only with a net.
                  onEinfuegen={autorImChat && trip.data?.archived_at == null ? rueckrufe.onEinfuegen : undefined}
                  onVeroeffentlichen={entwurf ? rueckrufe.onVeroeffentlichen : undefined}
                  // Wer das liest, entscheidet über den Wortlaut, und nur diese
                  // Seite kennt beide Blickwinkel: der Mitleser soll nicht
                  // glauben, eine persönliche Nachricht gehe an alle -- und
                  // der Autor sieht in ihrem Verlauf sonst nicht, was an alle
                  // ging und was nur an sie.
                  // Der Rückfall nennt keine Person, sondern „diese Person":
                  // solange der Name noch nicht da ist, wäre jedes Fürwort
                  // geraten -- und geraten heißt hier: bei jedem zweiten falsch.
                  directLabel={
                    withUser
                      ? t.chat.nurAn(partnerName ?? t.chat.dieserPerson)
                      : t.chat.nurAnDich
                  }
                  markiereBroadcast={trip.data?.mark_broadcast ?? false}
                  onReply={rueckrufe.onReply}
                  onJump={rueckrufe.onJump}
                  // The day separator's sheet (#113): the author, the trip
                  // chat and its draft, only with a net.
                  onTagestrenner={autorImChat ? rueckrufe.onTagestrenner : undefined}
                  /*
                   * **Nur an eigenen, nicht gelöschten Nachrichten** (#30).
                   * Der Kreis ist eine Bequemlichkeit; die Sperre steht im
                   * Server und bleibt dort.
                   */
                  auswahl={
                    // Moving takes only posts to everyone: a reply has no
                    // place of its own to go to. Sending to everyone takes
                    // only private messages (1.167.0).
                    auswahl !== null && message.own && !message.deleted &&
                    (auswahlZweck === "loeschen" ||
                      message.kind === (auswahlZweck === "anAlle" ? "direct" : "broadcast"))
                      ? { gewaehlt: auswahl.has(message.id), waehle: rueckrufe.waehle }
                      : undefined
                  }
                  onLoeschmodus={rueckrufe.onLoeschmodus}
                  onMenueAuf={rueckrufe.onMenueAuf}
                  onVerschiebemodus={kannBewegen ? rueckrufe.onVerschiebemodus : undefined}
                  onPublishToAll={autorImPrivatchat ? rueckrufe.onPublishToAll : undefined}
                  onSaveMedia={rueckrufe.onSaveMedia}
                  ziel={
                    wartendeBewegung !== null && message.kind === "broadcast" && !message.deleted && !wartendeBewegung.ids.includes(message.id)
                      ? rueckrufe.ziel
                      : undefined
                  }
                  wirdVerschoben={wartendeBewegung?.ids.includes(message.id) ?? false}
                  onWetter={autorImChat ? rueckrufe.onWetter : undefined}
                  onAbstimmung={autorImChat ? rueckrufe.onAbstimmung : undefined}
                  onTag={autorImChat ? rueckrufe.onTag : undefined}
                  onGif={autorImChat ? rueckrufe.onGif : undefined}
                  // A collage is its writer's, reader or author (1.168.0):
                  // the menu offers it on one's own posts only.
                  onCollage={rueckrufe.onCollage}
                  onAufDieKarte={autorImChat ? rueckrufe.onAufDieKarte : undefined}
                />
                {/* **The divider is a button** (1.19.0): it opens the list of
                    days, and a tap on a day lands at its first message.
                    The look stays; the hit area grows to 44 in CSS. */}
                {startsDay && !entwurf && (
                  <button
                    type="button"
                    className="day-divider"
                    data-tag={tagKey(dayMoment(message))}
                    title={t.chat.tage.trenner}
                    aria-label={`${dayLabel(dayMoment(message))}. ${t.chat.tage.trenner}`}
                    onClick={() => setTageswahl(tagKey(dayMoment(message)))}
                  >
                    {dayLabel(dayMoment(message))}
                  </button>
                )}
                {/* In the draft (#85) the day is a label, not a button: the
                    day list belongs to the chat. */}
                {startsDay && entwurf && (
                  <div className="day-divider">{dayLabel(dayMoment(message))}</div>
                )}
                {/* The report's divider („Reisebericht vom …", #85) stood here
                    until 1.85.0. Gone from use: the day separator (#113) and
                    the day divider are enough. The server still sends
                    `report`; nothing reads it. */}
                {/* **Die Trennlinie steht nach der Blase** (#35).

                    Der Verlauf läuft umgekehrt (`column-reverse`): was hier
                    unter der Blase steht, erscheint im Bild **über** ihr --
                    genau wie die Datumszeile eine Zeile höher. Sie markiert
                    damit den Anfang des Ungelesenen und nicht sein Ende. */}
                {message.id === ersteUngelesene && (
                  <div className="neu-trenner">
                    <span>{t.chat.neueNachrichten}</span>
                  </div>
                )}
              </div>
            );
          })}

          {/* **Keine stille Absage.** Ist die Suche nach dem Umschlagpunkt an
              ihrer Grenze angekommen, steht das da -- sonst sähe es aus wie
              ein Verlauf ohne Ungelesenes, und genau so las sich der Befund
              aus dem Betrieb. */}
          {kanteAufgegeben && olderCursor != null && (
            <p className="notice notice--rand">{t.chat.ungelesenJenseits}</p>
          )}

          {olderCursor != null && (
            <button
              type="button"
              className="button button--outline button--mittig"
              disabled={older.isPending}
              onClick={() => older.mutate(olderCursor)}
            >
              {older.isPending ? t.chat.wirdGeladen : t.chat.aeltere}
            </button>
          )}
        </div>
      </div>

      {/* Hier stand lange der Satz „Schreiben kannst du im Reise-Chat an
          alle." -- der Verlauf war einseitig: sie konnte ihm schreiben, er ihr
          nicht. Jetzt geht auch die andere Richtung, und was hier hinausgeht,
          sieht ausschließlich diese eine Person. */}
      {/* Unmittelbar über der Eingabezeile: dort steht er neben der Handlung,
          um die es geht, und liegt in keinem Browser unter einer Leiste. Nicht
          im Composer selbst, denn wo statt des Feldes ein Grund steht
          (abgeschlossene Reise, pausierte Empfängerin), gehört er trotzdem
          hin. */}
      <Netzstreifen />
      {/* The zone suggestion (1.87.0): the author, in the trip's chat. */}
      {withUser === undefined && !entwurf && trip.data?.role === "sender" && (
        <Zonenvorschlag trip={trip.data} />
      )}
      {stockt && (
        <p className="netzstreifen" role="status" data-schluessel="serverstockt">
          {t.chat.serverStockt}
        </p>
      )}

      {zuVeroeffentlichen !== null && (
        <Veroeffentlichen
          tripId={tripId}
          bis={zuVeroeffentlichen}
          entwuerfe={list}
          onFertig={(firstId) => {
            setZuVeroeffentlichen(null);
            if (firstId !== null) jumpBegins();
            navigate(firstId === null ? `/t/${tripId}` : `/t/${tripId}?m=${firstId}`);
          }}
          onSchliessen={() => setZuVeroeffentlichen(null)}
        />
      )}

      {/* **Keine stille Absage**: what the server refused stands as a line. */}
      {trennerFehler !== null && (
        <p className="auswahlleiste__hinweis" role="alert" data-chat-hinweis="">
          {trennerFehler}
        </p>
      )}

      {abstimmungAenderung !== null && abstimmungAenderung.poll && (
        <Abstimmungsformular
          tripId={tripId}
          entwurf={entwurf}
          vorbelegt={abstimmungAenderung.poll}
          onZu={() => setAbstimmungAenderung(null)}
        />
      )}

      {collageAenderung !== null && (
        <CollageSheet
          tripId={tripId}
          reiseVersatz={trip.data?.zones ?? null}
          message={collageAenderung}
          laeuft={collageAendern.isPending}
          fehler={collageFehler}
          onFertig={(mediaIds, collage) => {
            setCollageFehler(null);
            collageAendern.mutate(
              { id: collageAenderung.id, mediaIds, collage },
              {
                onSuccess: () => setCollageAenderung(null),
                onError: (fehler) => setCollageFehler(t.chat.collage.speichernFehler(fehlertext(fehler))),
              },
            );
          }}
          onZu={() => setCollageAenderung(null)}
        />
      )}

      {gifAenderung !== null && (
        <Gifwahl
          tripId={tripId}
          onGewaehlt={(media) => {
            const ziel = gifAenderung;
            setGifAenderung(null);
            gifTauschen.mutate(
              { messageId: ziel.id, mediaId: media.id },
              { onError: (fehler) => setTrennerFehler(fehler.message) },
            );
          }}
          onZu={() => setGifAenderung(null)}
        />
      )}

      {wetterAenderung !== null && wetterAenderung.weather && (
        <Wetterwahl
          tripId={tripId}
          versatz={trip.data?.zones ?? null}
          vorbelegt={{
            ort: {
              name: wetterAenderung.weather.place,
              gegend: [wetterAenderung.weather.region, wetterAenderung.weather.country].filter((s) => s !== "").join(", "),
              lat: wetterAenderung.weather.lat,
              lon: wetterAenderung.weather.lon,
            },
            at: wetterAenderung.weather.observed_at ?? wetterAenderung.created_at,
            tz_offset_min: wetterAenderung.weather.tz_offset_min,
          }}
          onWahl={(wahl) => {
            const ziel = wetterAenderung;
            setWetterAenderung(null);
            wetterAendern.mutate(
              { messageId: ziel.id, ...wahl },
              { onError: (fehler) => setTrennerFehler(fehler.message) },
            );
          }}
          onAbbruch={() => setWetterAenderung(null)}
        />
      )}

      {trennerAenderung !== null && trennerAenderung.day_separator && (
        <Tagestrennerwahl
          aendern
          vorgabe={trennerAenderung.day_separator}
          laeuft={trennerAendern.isPending}
          fehler={trennerFehler}
          onAbbruch={() => {
            setTrennerAenderung(null);
            setTrennerFehler(null);
          }}
          onWahl={(angaben) => {
            trennerAendern.mutate(
              { aktion: "aendern", messageId: trennerAenderung.id, angaben },
              {
                onSuccess: () => setTrennerAenderung(null),
                onError: (fehler) => setTrennerFehler(fehlertext(fehler)),
              },
            );
          }}
        />
      )}

      {tageswahl !== null && (
        <Tageswahl
          tripId={tripId}
          withUser={withUser}
          aktuell={tageswahl}
          onSchliessen={() => setTageswahl(null)}
          onWahl={(id) => {
            setTageswahl(null);
            // The same door as a search hit and a notification: `?m=` on the
            // page that is open. `springeZu()` loads what it takes.
            jumpBegins();
            navigate(`${window.location.pathname}?m=${id}`);
          }}
        />
      )}

      {/* **Die Hinweiszeile ist weg.** Hier stand „Was du hier schreibst,
          sieht nur X." als eigener Absatz über der Eingabezeile. Aus dem
          Betrieb: „diese Hinweiszeile entfernen und durch ein Symbol
          ersetzen in der leeren Nachrichtenzeile". Sie steht jetzt als
          Sinnbild im leeren Feld und geht mit dem ersten Zeichen weg -- wie
          der Platzhalter, dessen Aussage sie ohnehin verdoppelt hat. */}

      {/*
        * **Die Leiste ersetzt die Eingabezeile, sie legt sich nicht darüber**
        * (#30). Zwei Leisten übereinander nehmen dem Verlauf auf 390 Punkten
        * die halbe Höhe — und schreiben kann man ohnehin nicht, während man
        * auswählt.
        */}
      {loeschbefund !== null && (
        <p className="auswahlleiste__hinweis" role="alert">
          {loeschbefund}
        </p>
      )}

      {bewegeZiel !== null && wartendeBewegung !== null && (
        <Verschiebefrage
          anzahl={wartendeBewegung.ids.length}
          ziel={t.chat.verschieben.ziel(
            datumText(dayMoment(bewegeZiel), trip.data?.zones ?? null),
            bewegeZiel.body.replace(/\s+/g, " ").trim().slice(0, 60),
          )}
          onAbbruch={() => setBewegeZiel(null)}
          onWahl={async (place) => {
            const ziel = bewegeZiel;
            const ids = wartendeBewegung.ids;
            setBewegeZiel(null);
            if (!online) {
              setBewegeFehler(t.allgemein.ohneNetz);
              return;
            }
            try {
              await bewegen.mutateAsync({ ids, target_id: ziel.id, place });
              endMove();
              setBewegeFehler(null);
              // To the moved posts, wherever they stand now.
              springeZu(ids[0]!);
            } catch (fehler) {
              setBewegeFehler(fehlertext(fehler));
            }
          }}
        />
      )}

      {auswahl !== null ? (
        <Auswahlleiste
          anzahl={auswahl.size}
          laeuft={loeschen.isPending || anAlle.isPending}
          online={online}
          zweck={auswahlZweck}
          onAnAlle={() => void anAlleSenden([...auswahl])}
          onVerschieben={() => {
            // Only the author's own posts to everyone can be picked here,
            // in the order of the chat.
            beginMove(
              tripId,
              list.filter((m) => auswahl.has(m.id) && m.kind === "broadcast").map((m) => m.id),
              entwurf,
            );
            setAuswahl(null);
            setBewegeFehler(null);
          }}
          onAbbrechen={() => {
            setAuswahl(null);
            setLoeschbefund(null);
          }}
          onLoeschen={async () => {
            const ids = [...auswahl];
            if (!(await rueckfrage(t.chat.loeschenFrage(t.chat.auswahlWorte(ids.length, 0)), t.chat.loeschenKnopf))) {
              return;
            }
            // A day separator is a post (#130): it goes like any other.
            const befund = await loeschen.mutateAsync(ids);
            if (befund.geblieben.length === 0) {
              setAuswahl(null);
              setLoeschbefund(null);
              return;
            }
            // **Keine stille Absage.** Was nicht durchkam, bleibt markiert,
            // und die Zahl daneben sagt, wie viel durchkam.
            setAuswahl(new Set(befund.geblieben));
            setLoeschbefund(t.chat.teilweiseGeloescht(ids.length - befund.geblieben.length, ids.length));
          }}
        />
      ) : wartendeBewegung !== null ? (
        <Verschiebeleiste
          anzahl={wartendeBewegung.ids.length}
          online={online}
          fehler={bewegeFehler}
          onAbbrechen={() => {
            endMove();
            setBewegeFehler(null);
          }}
        />
      ) : !wartetAufAuskunft ? (
        <Composer
          meldezeile={meldezeile}
          tripId={tripId}
          withUser={withUser}
          bezug={bezug}
          onBezugWeg={() => setBezug(null)}
          onGesendet={() => {
            // A reader's own line is an answer (`reply`); under "only posts
            // to everyone" it would vanish the moment it is sent (1.93.0,
            // #114). Sending shows everything again.
            if (filter?.art === "beitraege") setFilter(null, 0);
            nachUntenSpringen();
          }}
          entwurf={entwurf}
          insertion={
            einfuegen === null
              ? null
              : {
                  ...einfuegen,
                  text: einfuegenText,
                  onDrop: () => setEinfuegen(null),
                  // To the new post, wherever it stands (#130).
                  onDone: (id) => springeZu(id),
                }
          }
          tagAenderung={tagAenderung}
          onTagAenderungFertig={() => setTagAenderung(null)}
          disabled={trip.data?.archived_at != null || stumm !== null || entwurfAus}
          // Die abgeschlossene Reise hat Vorrang: sie gilt für alle Verläufe,
          // und ein Grund, der nur diesen einen betrifft, wäre daneben falsch.
          disabledHint={
            entwurfAus ? (
              <span data-entwurf-aus="">
                {t.chat.entwurf.ausgeschaltet}{" "}
                <Link to={`/t/${tripId}/info`}>{t.chat.aufDerReiseseite}</Link>
              </span>
            ) : trip.data?.archived_at != null ? undefined : (
              <>
                {stumm?.text.replace("{name}", partnerName ?? t.chat.diesePerson)}
                {stumm?.zurReise && (
                  <>
                    {" "}
                    <Link to={`/t/${tripId}/info`}>
                      {t.chat.aufDerReiseseite}
                    </Link>
                  </>
                )}
              </>
            )
          }
          // Von lang nach kurz — der Composer nimmt die längste Fassung,
          // die in die Zeile passt. „Nachricht an alle Empfänger" wurde auf
          // dem Telefon abgeschnitten; abgeschnitten sagt weniger als kurz.
          // **Wer es sehen wird** -- dieselbe Aussage wie der Platzhalter
          // daneben, einmal als Bild. Ein Empfänger oder alle: der private
          // Verlauf und die Antwort eines Mitlesenden gehen an einen, der
          // Beitrag des Autors an alle.
          reichweite={
            withUser === undefined && trip.data?.role === 'sender' ? 'alle' : 'einer'
          }
          platzhalter={
            entwurf
              ? t.chat.entwurf.platzhalter
              : withUser
              ? t.chat.platzhalterAnEinen(partnerName ?? t.chat.dieserPerson)
              : trip.data === undefined
                ? t.chat.platzhalterUnbekannt
                : trip.data.role === "sender"
                  ? t.chat.platzhalterAnAlle
                  : t.chat.platzhalterAntwort(trip.data.owner.display_name)
          }
        />
      ) : null}
    </>
  );
}

/**
 * Warum an diesen Menschen gerade nichts geht — oder null, wenn es geht.
 *
 * Die Reihenfolge ist die der Endgültigkeit: wer gar nicht mehr dabei ist,
 * bleibt es; ein stillgelegtes Konto kann die Verwaltung wieder freigeben; ein
 * Stichtag ist ein Handgriff auf der Reiseseite. Genannt wird immer der
 * Zustand, der die Nachricht **zuerst** aufhält.
 *
 * `{name}` wird von der Seite ersetzt — hier steht der Name noch nicht fest,
 * weil er aus derselben Liste kommt, die auch diesen Zustand liefert.
 */
function grundOhneZeile(
  partner: Member | undefined,
): { text: string; zurReise: boolean } | null {
  if (partner === undefined) {
    return {
      text: WORTLAUT.chat.nichtMehrDabei,
      zurReise: false,
    };
  }
  // **Selbst ausgestiegen — und das musste hierher.** Seit die
  // Empfängerliste Ausgestiegene mitführt (damit sie nicht spurlos fehlen),
  // *findet* diese Seite den Partner wieder: ohne diesen Fall fiele er durch
  // alle Prüfungen, und die Eingabezeile stünde offen, obwohl niemand mehr
  // dahinter ist. Aus dem Betrieb gemeldet, einen Tag nach dem Umbau.
  if (partner.left === true) {
    return {
      text: WORTLAUT.chat.ausgestiegen,
      zurReise: false,
    };
  }
  if (partner.disabled) {
    return {
      text: WORTLAUT.chat.stillgelegt,
      zurReise: false,
    };
  }
  if (partner.cut_off) {
    return {
      text: WORTLAUT.chat.ausDemVerteiler,
      zurReise: true,
    };
  }
  return null;
}
